import React, { useState, useRef } from 'react'
import { mediaAPI } from '../services/api'
import { MEDIA_BASE_URL } from './Gallery'
import './ImageUploader.css'

/**
 * Client-side image uploader for flight-log galleries.
 *
 * For each selected image it generates a downscaled thumbnail on a canvas,
 * requests presigned S3 URLs from the media Lambda (folder derived from the
 * flight date + site), PUTs the full image and thumbnail straight to S3, then
 * merges the filenames into the gallery's manifest.json.
 *
 * Props:
 *   date, site   - used to derive the shared gallery folder
 *   onUploaded(folder, filenames) - called after a successful batch
 *
 * Requires date + site to be set before uploading (so the folder is stable).
 */
const THUMB_MAX = 400 // px, longest edge

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

async function makeThumbnail(file) {
  const img = await loadImage(file)
  const scale = Math.min(1, THUMB_MAX / Math.max(img.width, img.height))
  const w = Math.round(img.width * scale)
  const h = Math.round(img.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  canvas.getContext('2d').drawImage(img, 0, 0, w, h)
  URL.revokeObjectURL(img.src)
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8))
}

async function putToS3(url, blob, type) {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': type },
    body: blob,
  })
  if (!res.ok) throw new Error(`Upload failed (${res.status})`)
}

function ImageUploader({ date, site, onUploaded }) {
  const [status, setStatus] = useState('idle') // idle | uploading | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [uploaded, setUploaded] = useState([]) // [{ name, url }]
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  const canUpload = Boolean(date && site)

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'))
    if (files.length === 0) return
    if (!canUpload) {
      setError('Please set the flight date and site before uploading images.')
      return
    }
    setError(null)
    setStatus('uploading')
    setProgress({ done: 0, total: files.length })

    try {
      // 1) presign all files
      const presignReq = files.map((f) => ({ name: f.name, type: f.type }))
      const { folder, uploads } = await mediaAPI.presign(date, site, presignReq)

      // 2) upload full + thumb for each
      const doneNames = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const slot = uploads[i]
        if (!slot) continue
        const thumb = await makeThumbnail(file).catch(() => null)
        await putToS3(slot.fullUrl, file, file.type)
        if (thumb) await putToS3(slot.thumbUrl, thumb, 'image/jpeg')
        doneNames.push(slot.name)
        setProgress({ done: i + 1, total: files.length })
      }

      // 3) update the manifest
      await mediaAPI.updateManifest(folder, doneNames)

      const previews = doneNames.map((name) => ({
        name,
        url: `${MEDIA_BASE_URL}/images/${folder}/thumbs/${encodeURIComponent(name)}`,
      }))
      setUploaded((prev) => [...prev, ...previews])
      setStatus('done')
      if (onUploaded) onUploaded(folder, doneNames)
    } catch (err) {
      setError(err.message || 'Upload failed')
      setStatus('error')
    }
  }

  return (
    <div className="image-uploader">
      {!canUpload && (
        <p className="uploader-hint">Set the flight date and site first, then add photos.</p>
      )}

      <div
        className={`uploader-drop ${canUpload ? '' : 'disabled'}`}
        onClick={() => canUpload && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault() }}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files) }}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          disabled={!canUpload}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {status === 'uploading'
          ? <span>Uploading {progress.done}/{progress.total}…</span>
          : <span>Click or drop images here to add them to this flight's gallery</span>}
      </div>

      {error && <p className="uploader-error">{error}</p>}

      {uploaded.length > 0 && (
        <div className="uploader-previews">
          {uploaded.map((u) => (
            <img key={u.name} src={u.url} alt={u.name} loading="lazy" />
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageUploader
